import { FeedLetConfig, ContextualMarker, ViewportInfo } from './types'

export interface ThreadData {
  type: 'feedback' | 'contextual' | 'comment' | 'exit-survey'
  position: { x: number; y: number }
  marker?: ContextualMarker
  viewportInfo?: ViewportInfo
  placeholder?: string
}

export class ThreadInterface {
  private config: FeedLetConfig
  private element: HTMLElement | null = null
  private isOpen = false
  private eventListeners: Map<string, Function[]> = new Map()
  private onClose: (() => void) | null = null
  private uploadedFiles: File[] = []
  private screenshots: Blob[] = []
  private currentThreadData: ThreadData | null = null
  private isDragging = false
  private dragOffset = { x: 0, y: 0 }
  private dragStartPosition = { x: 0, y: 0 }

  constructor(config: FeedLetConfig) {
    this.config = config
  }

  open(data: ThreadData, onClose?: () => void) {
    if (this.isOpen) return

    console.log('ExitBug: ThreadInterface.open called with data:', data)
    this.isOpen = true
    this.onClose = onClose || null
    this.currentThreadData = data // Store the thread data for submission

    this.createElement(data)
    this.addStyles()
    this.attachEventListeners()
    
    document.body.appendChild(this.element!)
    
    // Position the thread interface
    this.positionElement(data.position)
    
    // Focus on the textarea
    const textarea = this.element?.querySelector('.feedlet-thread-textarea') as HTMLTextAreaElement
    if (textarea) {
      textarea.focus()
    }

    // Trigger opening animation
    requestAnimationFrame(() => {
      this.element?.classList.add('open')
    })

    this.emit('opened', { type: data.type })
  }

  close() {
    if (!this.isOpen) return

    this.isOpen = false
    
    // Clear files and screenshots
    this.uploadedFiles = []
    this.screenshots = []
    
    // Clear current thread data
    this.currentThreadData = null
    
    if (this.element) {
      this.element.classList.remove('open')
      
      setTimeout(() => {
        if (this.element) {
          this.element.remove()
          this.element = null
        }
      }, 200)
    }

    if (this.onClose) {
      this.onClose()
    }

    this.emit('closed', {})
  }

  public on(event: string, callback: Function): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, [])
    }
    this.eventListeners.get(event)!.push(callback)
  }

  private emit(event: string, data: any): void {
    const listeners = this.eventListeners.get(event)
    if (listeners) {
      listeners.forEach(callback => callback(data))
    }
  }

  public isThreadOpen(): boolean {
    return this.isOpen
  }

  private createElement(data: ThreadData) {
    this.element = document.createElement('div')
    this.element.className = 'feedlet-thread-container'
    this.element.id = 'feedlet-thread'

    const isContextual = data.type === 'contextual'
    const isComment = data.type === 'comment'
    const isExitSurvey = data.type === 'exit-survey'
    const hasMarker = isContextual && data.marker
    const placeholder = data.placeholder || 
      (isContextual ? 'Add a comment...' : 
        (isComment ? 'Add a comment...' : 
          (isExitSurvey ? 'What could we have done better?' : 'Start a new thread...')))

    this.element.innerHTML = `
      <div class="feedlet-thread-box">
        <div class="feedlet-thread-header">
          <div class="feedlet-thread-title">
            ${hasMarker ? this.renderContextualTitle(data.marker!) : 
              (isExitSurvey ? '🚪 Quick Survey' : 
                (isContextual || isComment ? 'Comment' : 'Feedback'))}
          </div>
          <button type="button" class="feedlet-thread-close-btn" title="Close">
            <svg width="16" height="16" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
          </button>
        </div>

        ${isExitSurvey ? this.renderExitSurveyIntro() : ''}
        <form class="feedlet-thread-form" id="feedlet-thread-form">
          <div class="feedlet-thread-input-container">
            <textarea
              class="feedlet-thread-textarea"
              placeholder="${placeholder}"
              rows="1"
              required
            ></textarea>
            <div class="feedlet-thread-attachments" id="feedlet-attachments"></div>
            <div class="feedlet-thread-actions">
              ${isExitSurvey ? '' : `
                <button type="button" class="feedlet-thread-action-btn" id="feedlet-file-upload" title="Add file">
                  <svg width="18" height="18" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd"/>
                  </svg>
                </button>
                <button type="button" class="feedlet-thread-action-btn" id="feedlet-screenshot" title="Add screenshot (multiple allowed)">
                  <svg width="18" height="18" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd"></path>
                  </svg>
                </button>
                <button type="button" class="feedlet-thread-action-btn" title="Format text">
                  <svg width="18" height="18" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h8a1 1 0 110 2H4a1 1 0 01-1-1z"></path>
                  </svg>
                </button>
              `}
              <button type="submit" class="feedlet-thread-submit-btn" title="${isExitSurvey ? 'Submit survey' : 'Send'}">
                <svg width="18" height="18" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"></path>
                </svg>
              </button>
            </div>
          </div>
          ${data.type === 'feedback' ? this.renderFeedbackTypeSelector() : ''}
          ${!isExitSurvey ? this.renderContactSection() : this.renderExitSurveyContact()}
        </form>
        <input type="file" id="feedlet-file-input" accept="image/*,.pdf,.doc,.docx,.txt" multiple style="display: none;">
      </div>
      <div class="feedlet-thread-backdrop" data-dismiss="true"></div>
    `
  }

  private renderContextualTitle(marker: ContextualMarker): string {
    const elementInfo = marker.elementText ? `"${marker.elementText}"` : marker.elementSelector
    return `
      <div class="feedlet-thread-title-content">
        <svg width="12" height="12" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd"></path>
        </svg>
        <span class="feedlet-thread-title-text">${elementInfo}</span>
      </div>
    `
  }

  private renderContextualInfo(marker: ContextualMarker): string {
    const elementInfo = marker.elementText ? `"${marker.elementText}"` : marker.elementSelector
    return `
      <div class="feedlet-thread-context">
        <div class="feedlet-thread-context-pin">
          <svg width="12" height="12" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd"></path>
          </svg>
        </div>
        <div class="feedlet-thread-context-text">
          ${elementInfo}
        </div>
      </div>
    `
  }

  private renderFeedbackTypeSelector(): string {
    return `
      <div class="feedlet-thread-type-selector">
        <label class="feedlet-thread-type-option">
          <input type="radio" name="type" value="bug" checked>
          <span class="feedlet-thread-type-label">
            <span class="feedlet-thread-type-icon">🐛</span>
            Bug
          </span>
        </label>
        <label class="feedlet-thread-type-option">
          <input type="radio" name="type" value="idea">
          <span class="feedlet-thread-type-label">
            <span class="feedlet-thread-type-icon">💡</span>
            Idea
          </span>
        </label>
        <label class="feedlet-thread-type-option">
          <input type="radio" name="type" value="other">
          <span class="feedlet-thread-type-label">
            <span class="feedlet-thread-type-icon">💬</span>
            Other
          </span>
        </label>
      </div>
    `
  }

  private renderContactSection(): string {
    return `
      <div class="feedlet-thread-contact-section">
        <details class="feedlet-thread-contact-details">
          <summary class="feedlet-thread-contact-summary">
            <svg width="16" height="16" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"></path>
              <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"></path>
            </svg>
            Get notified when this is resolved
          </summary>
          <div class="feedlet-thread-contact-content">
            <p class="feedlet-thread-contact-description">
              Want to know when we've addressed your feedback? Leave your email and we'll keep you updated.
            </p>
            <div class="feedlet-thread-contact-fields">
              <input 
                type="email" 
                name="contact_email" 
                placeholder="your.email@example.com"
                class="feedlet-thread-contact-email"
              />
              <label class="feedlet-thread-contact-notify">
                <input type="checkbox" name="notify_on_update" value="true" checked />
                <span>Send me updates</span>
              </label>
            </div>
          </div>
        </details>
      </div>
    `
  }

  private renderExitSurveyIntro(): string {
    return `
      <div class="feedlet-thread-exit-intro">
        <div class="feedlet-thread-exit-intro-content">
          <p>Before you go, could you help us improve? Your feedback only takes a moment and helps us serve you better.</p>
        </div>
      </div>
    `
  }

  private renderExitSurveyContact(): string {
    return `
      <div class="feedlet-thread-contact-section feedlet-thread-exit-contact">
        <details class="feedlet-thread-contact-details">
          <summary class="feedlet-thread-contact-summary">
            <svg width="16" height="16" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"></path>
              <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"></path>
            </svg>
            Stay in touch (optional)
          </summary>
          <div class="feedlet-thread-contact-content">
            <p class="feedlet-thread-contact-description">
              Want to hear about improvements based on your feedback? Leave your email.
            </p>
            <div class="feedlet-thread-contact-fields">
              <input 
                type="email" 
                name="contact_email" 
                placeholder="your.email@example.com"
                class="feedlet-thread-contact-email"
              />
              <label class="feedlet-thread-contact-notify">
                <input type="checkbox" name="notify_on_update" value="true" />
                <span>Send me updates</span>
              </label>
            </div>
          </div>
        </details>
      </div>
    `
  }

  private positionElement(position: { x: number; y: number }) {
    if (!this.element) return

    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight
    }

    const elementRect = this.element.getBoundingClientRect()
    const elementWidth = 360 // Approximate width
    const elementHeight = 200 // Approximate height

    let x = position.x
    let y = position.y

    // Keep within viewport bounds
    if (x + elementWidth > viewport.width - 20) {
      x = viewport.width - elementWidth - 20
    }
    if (x < 20) {
      x = 20
    }

    if (y + elementHeight > viewport.height - 20) {
      y = position.y - elementHeight - 10
    }
    if (y < 20) {
      y = 20
    }

    this.element.style.left = `${x}px`
    this.element.style.top = `${y}px`
  }

  private addStyles() {
    if (document.getElementById('feedlet-thread-styles')) return

    const style = document.createElement('style')
    style.id = 'feedlet-thread-styles'
    style.textContent = `
      .feedlet-thread-container {
        position: fixed;
        z-index: 1000000;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        opacity: 0;
        transform: scale(0.96) translateY(-8px);
        transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
        pointer-events: none;
      }

      .feedlet-thread-container.open {
        opacity: 1;
        transform: scale(1) translateY(0);
        pointer-events: auto;
      }

      .feedlet-thread-container.dragging {
        transition: none;
      }

      .feedlet-thread-backdrop {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        z-index: -1;
        background: transparent;
      }

      .feedlet-thread-box {
        width: 360px;
        background: var(--feedlet-bg-primary, #1a1a1a);
        border-radius: 12px;
        box-shadow: 0 8px 16px var(--feedlet-shadow, rgba(0, 0, 0, 0.15));
        border: 1px solid var(--feedlet-border, rgba(255, 255, 255, 0.12));
        overflow: hidden;
        backdrop-filter: var(--feedlet-backdrop-filter, blur(8px));
      }

      .feedlet-thread-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px 20px;
        border-bottom: 1px solid var(--feedlet-border, #333);
        position: relative;
        z-index: 10;
        cursor: move;
        user-select: none;
      }

      .feedlet-thread-header:hover {
        background: var(--feedlet-border-light, rgba(255, 255, 255, 0.03));
      }

      .feedlet-thread-header.dragging {
        cursor: grabbing;
        background: var(--feedlet-border-light, rgba(255, 255, 255, 0.05));
      }

      .feedlet-thread-title {
        color: var(--feedlet-text-primary, #ffffff);
        font-size: 14px;
        font-weight: 600;
        flex: 1;
        pointer-events: none;
      }

      .feedlet-thread-title-content {
        display: flex;
        align-items: center;
        gap: 8px;
        pointer-events: none;
      }

      .feedlet-thread-title-text {
        font-family: 'SF Mono', Monaco, 'Cascadia Code', monospace;
        font-size: 12px;
        background: var(--feedlet-bg-secondary, #333);
        padding: 2px 6px;
        border-radius: 4px;
        color: var(--feedlet-text-secondary, #ccc);
        max-width: 240px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        pointer-events: none;
      }

      .feedlet-thread-close-btn {
        background: none;
        border: none;
        color: var(--feedlet-text-tertiary, #9CA3AF);
        cursor: pointer;
        padding: 4px;
        border-radius: 6px;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        flex-shrink: 0;
        pointer-events: auto;
      }

      .feedlet-thread-close-btn:hover {
        background: var(--feedlet-border-light, rgba(255, 255, 255, 0.1));
        color: var(--feedlet-text-primary, #ffffff);
      }

      .feedlet-thread-context {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 12px 16px;
        background: var(--feedlet-bg-secondary, #2a2a2a);
        border-bottom: 1px solid var(--feedlet-border, #333);
        font-size: 13px;
        color: var(--feedlet-text-tertiary, #999);
      }

      .feedlet-thread-context-pin {
        color: ${this.config.color || '#4f46e5'};
        display: flex;
        align-items: center;
      }

      .feedlet-thread-context-text {
        font-family: 'SF Mono', Monaco, 'Cascadia Code', monospace;
        font-size: 12px;
        background: var(--feedlet-bg-secondary, #333);
        padding: 2px 6px;
        border-radius: 4px;
        color: var(--feedlet-text-secondary, #ccc);
        max-width: 280px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .feedlet-thread-form {
        padding: 20px;
      }

      .feedlet-thread-input-container {
        position: relative;
      }

      .feedlet-thread-textarea {
        width: 100%;
        background: transparent;
        border: none;
        outline: none;
        resize: none;
        font-size: 16px;
        line-height: 1.5;
        color: var(--feedlet-text-primary, #fff);
        padding: 0 0 12px 0;
        font-family: inherit;
        min-height: 24px;
        max-height: 120px;
      }

      .feedlet-thread-textarea::placeholder {
        color: var(--feedlet-text-tertiary, #666);
      }

      .feedlet-thread-attachments {
        margin: 8px 0;
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
      }

      .feedlet-thread-attachment {
        display: flex;
        align-items: center;
        gap: 6px;
        background: #2a2a2a;
        border: 1px solid #333;
        border-radius: 6px;
        padding: 6px 8px;
        font-size: 12px;
        color: #ccc;
        max-width: 200px;
      }

      .feedlet-thread-attachment-name {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        flex: 1;
      }

      .feedlet-thread-attachment-remove {
        background: none;
        border: none;
        color: #666;
        cursor: pointer;
        padding: 2px;
        border-radius: 3px;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 16px;
        height: 16px;
      }

      .feedlet-thread-attachment-remove:hover {
        background: #444;
        color: #fff;
      }

      .feedlet-thread-attachment-preview {
        width: 100%;
        max-width: 200px;
        height: 60px;
        object-fit: cover;
        border-radius: 4px;
        background: #333;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-top: 4px;
      }

      .feedlet-thread-exit-intro {
        padding: 16px 20px;
        background: var(--feedlet-bg-secondary, #2a2a2a);
        border-bottom: 1px solid var(--feedlet-border, #333);
      }

      .feedlet-thread-exit-intro-content p {
        margin: 0;
        color: var(--feedlet-text-secondary, #d1d5db);
        font-size: 14px;
        line-height: 1.5;
        text-align: center;
      }

      .feedlet-thread-actions {
        display: flex;
        align-items: center;
        justify-content: space-between;
        border-top: 1px solid var(--feedlet-border, #333);
        padding-top: 12px;
      }

      .feedlet-thread-action-btn {
        background: none;
        border: none;
        color: #999;
        cursor: pointer;
        padding: 8px;
        border-radius: 6px;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .feedlet-thread-action-btn:hover {
        background: #333;
        color: #fff;
      }

      .feedlet-thread-submit-btn {
        background: ${this.config.color || '#4f46e5'};
        border: none;
        color: white;
        cursor: pointer;
        padding: 8px 12px;
        border-radius: 8px;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        min-width: 44px;
      }

      .feedlet-thread-submit-btn:hover {
        background: ${this.adjustColor(this.config.color || '#4f46e5', -20)};
        transform: translateY(-1px);
      }

      .feedlet-thread-submit-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        transform: none;
      }

      .feedlet-thread-type-selector {
        display: flex;
        gap: 8px;
        margin-top: 16px;
        padding-top: 16px;
        border-top: 1px solid #333;
      }

      .feedlet-thread-type-option {
        cursor: pointer;
        flex: 1;
      }

      .feedlet-thread-type-option input[type="radio"] {
        display: none;
      }

      .feedlet-thread-type-label {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 8px 12px;
        background: #2a2a2a;
        border: 1px solid #333;
        border-radius: 8px;
        color: #999;
        font-size: 13px;
        font-weight: 500;
        transition: all 0.2s ease;
        justify-content: center;
      }

      .feedlet-thread-type-option:hover .feedlet-thread-type-label {
        background: #333;
        color: #fff;
      }

      .feedlet-thread-type-option input[type="radio"]:checked + .feedlet-thread-type-label {
        background: ${this.config.color || '#4f46e5'}20;
        border-color: ${this.config.color || '#4f46e5'};
        color: ${this.config.color || '#4f46e5'};
      }

      .feedlet-thread-type-icon {
        font-size: 14px;
      }

      @media (max-width: 640px) {
        .feedlet-thread-box {
          width: calc(100vw - 40px);
          max-width: 360px;
        }
      }

      .feedlet-thread-contact-section {
        margin-top: 16px;
        padding-top: 16px;
        border-top: 1px solid #333;
      }

      .feedlet-thread-contact-details {
        border: none;
        background: none;
      }

      .feedlet-thread-contact-summary {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 13px;
        color: #999;
        cursor: pointer;
        list-style: none;
        padding: 0;
        margin: 0;
        transition: color 0.2s;
      }

      .feedlet-thread-contact-summary:hover {
        color: #fff;
      }

      .feedlet-thread-contact-summary::-webkit-details-marker {
        display: none;
      }

      .feedlet-thread-contact-content {
        margin-top: 12px;
        padding: 12px;
        background: #2a2a2a;
        border-radius: 8px;
        border: 1px solid #333;
      }

      .feedlet-thread-contact-description {
        margin: 0 0 12px 0;
        font-size: 12px;
        color: #ccc;
        line-height: 1.5;
      }

      .feedlet-thread-contact-fields {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .feedlet-thread-contact-email {
        background: #1a1a1a;
        border: 1px solid #444;
        border-radius: 6px;
        padding: 8px 12px;
        color: #fff;
        font-size: 13px;
        transition: border-color 0.2s;
      }

      .feedlet-thread-contact-email:focus {
        outline: none;
        border-color: ${this.config.color || '#4f46e5'};
        box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.2);
      }

      .feedlet-thread-contact-email::placeholder {
        color: #666;
      }

      .feedlet-thread-contact-notify {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 12px;
        color: #ccc;
        cursor: pointer;
      }

      .feedlet-thread-contact-notify input[type="checkbox"] {
        width: 16px;
        height: 16px;
        border: 1px solid #666;
        border-radius: 3px;
        background: transparent;
        cursor: pointer;
        appearance: none;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s;
      }

      .feedlet-thread-contact-notify input[type="checkbox"]:checked {
        background: ${this.config.color || '#4f46e5'};
        border-color: ${this.config.color || '#4f46e5'};
      }

      .feedlet-thread-contact-notify input[type="checkbox"]:checked::after {
        content: '✓';
        color: white;
        font-size: 11px;
        font-weight: bold;
      }
    `

    document.head.appendChild(style)
  }

  private adjustColor(color: string, amount: number): string {
    const usePound = color[0] === '#'
    const col = usePound ? color.slice(1) : color
    const num = parseInt(col, 16)
    
    let r = (num >> 16) + amount
    let g = (num >> 8 & 0x00FF) + amount
    let b = (num & 0x0000FF) + amount
    
    r = r > 255 ? 255 : r < 0 ? 0 : r
    g = g > 255 ? 255 : g < 0 ? 0 : g
    b = b > 255 ? 255 : b < 0 ? 0 : b
    
    return (usePound ? '#' : '') + (r << 16 | g << 8 | b).toString(16).padStart(6, '0')
  }

  private attachEventListeners() {
    if (!this.element) return

    // Close button
    const closeBtn = this.element.querySelector('.feedlet-thread-close-btn')
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()
        console.log('ExitBug: Close button clicked')
        this.close()
      }, true)
    } else {
      console.warn('ExitBug: Close button not found')
    }

    // Close on backdrop click
    const backdrop = this.element.querySelector('.feedlet-thread-backdrop')
    if (backdrop) {
      backdrop.addEventListener('click', () => {
        this.close()
      })
    }

    // Drag functionality
    this.attachDragListeners()

    // File upload
    const fileUploadBtn = this.element.querySelector('#feedlet-file-upload')
    const fileInput = this.element.querySelector('#feedlet-file-input') as HTMLInputElement
    if (fileUploadBtn && fileInput) {
      fileUploadBtn.addEventListener('click', () => {
        fileInput.click()
      })

      fileInput.addEventListener('change', (event) => {
        const target = event.target as HTMLInputElement
        if (target.files && target.files.length > 0) {
          this.handleFileUpload(target.files)
        }
      })
    }

    // Screenshot area selection
    const screenshotBtn = this.element.querySelector('#feedlet-screenshot')
    if (screenshotBtn) {
      screenshotBtn.addEventListener('click', () => {
        this.handleScreenshotSelection()
      })
    }

    // Auto-resize textarea
    const textarea = this.element.querySelector('.feedlet-thread-textarea') as HTMLTextAreaElement
    if (textarea) {
      textarea.addEventListener('input', () => {
        textarea.style.height = 'auto'
        textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px'
      })

      // Close on Escape key
      textarea.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this.close()
        }
      })
    }

    // Global ESC key handler
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen) {
        this.close()
      }
    })

    // Form submission
    const form = this.element.querySelector('#feedlet-thread-form') as HTMLFormElement
    if (form) {
      form.addEventListener('submit', (event) => {
        console.log('ExitBug: Form submit event triggered')
        event.preventDefault()
        this.handleSubmit(form)
      })
    } else {
      console.warn('ExitBug: Form element not found')
    }

    // Submit button click (for debugging)
    const submitBtn = this.element.querySelector('.feedlet-thread-submit-btn')
    if (submitBtn) {
      submitBtn.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()
        console.log('ExitBug: Submit button clicked')
        
        // Manually trigger form submission
        const form = this.element?.querySelector('#feedlet-thread-form') as HTMLFormElement
        if (form) {
          console.log('ExitBug: Triggering form submission manually')
          this.handleSubmit(form)
        }
      }, true)
    } else {
      console.warn('ExitBug: Submit button not found')
    }
  }

  private attachDragListeners() {
    if (!this.element) return

    const header = this.element.querySelector('.feedlet-thread-header')
    if (!header) return

    const handleMouseDown = (e: MouseEvent) => {
      // Only start dragging if clicking on the header, not on the close button
      if ((e.target as HTMLElement).closest('.feedlet-thread-close-btn')) {
        return
      }

      this.isDragging = true
      this.dragStartPosition = { x: e.clientX, y: e.clientY }
      
      const rect = this.element!.getBoundingClientRect()
      this.dragOffset = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      }

      header.classList.add('dragging')
      this.element!.classList.add('dragging')
      
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      
      e.preventDefault()
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (!this.isDragging || !this.element) return

      const x = e.clientX - this.dragOffset.x
      const y = e.clientY - this.dragOffset.y

      // Keep within viewport bounds
      const viewport = {
        width: window.innerWidth,
        height: window.innerHeight
      }
      
      const elementRect = this.element.getBoundingClientRect()
      const elementWidth = elementRect.width
      const elementHeight = elementRect.height

      const clampedX = Math.max(0, Math.min(x, viewport.width - elementWidth))
      const clampedY = Math.max(0, Math.min(y, viewport.height - elementHeight))

      this.element.style.left = `${clampedX}px`
      this.element.style.top = `${clampedY}px`
      
      e.preventDefault()
    }

    const handleMouseUp = (e: MouseEvent) => {
      if (!this.isDragging) return

      this.isDragging = false
      header.classList.remove('dragging')
      this.element!.classList.remove('dragging')
      
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      
      e.preventDefault()
    }

    header.addEventListener('mousedown', handleMouseDown as EventListener)
  }

  private handleFileUpload(files: FileList) {
    const attachmentsContainer = this.element?.querySelector('#feedlet-attachments')
    if (!attachmentsContainer) return

    // Add files to the uploaded files array
    Array.from(files).forEach(file => {
      this.uploadedFiles.push(file)
      this.addFileToDisplay(file, attachmentsContainer)
    })
  }

  private addFileToDisplay(file: File, container: Element) {
    const fileElement = document.createElement('div')
    fileElement.className = 'feedlet-thread-attachment'
    fileElement.dataset.fileName = file.name

    const isImage = file.type.startsWith('image/')
    const fileIcon = isImage ? '🖼️' : '📎'

    fileElement.innerHTML = `
      ${isImage ? '<div class="feedlet-thread-attachment-preview" id="preview-' + Date.now() + '"></div>' : ''}
      <div style="display: flex; align-items: center; gap: 6px; width: 100%;">
        <span>${fileIcon}</span>
        <div class="feedlet-thread-attachment-name" title="${file.name}">${file.name}</div>
        <button type="button" class="feedlet-thread-attachment-remove" title="Remove">
          <svg width="12" height="12" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
          </svg>
        </button>
      </div>
    `

    // Add remove functionality
    const removeBtn = fileElement.querySelector('.feedlet-thread-attachment-remove')
    if (removeBtn) {
      removeBtn.addEventListener('click', () => {
        this.removeFile(file.name)
        fileElement.remove()
      })
    }

    // Add image preview if it's an image
    if (isImage) {
      const reader = new FileReader()
      reader.onload = (e) => {
        const preview = fileElement.querySelector(`#preview-${Date.now()}`) as HTMLElement
        if (preview && e.target?.result) {
          preview.innerHTML = `<img src="${e.target.result}" style="width: 100%; height: 60px; object-fit: cover; border-radius: 4px;" alt="Preview">`
        }
      }
      reader.readAsDataURL(file)
    }

    container.appendChild(fileElement)
  }

  private removeFile(fileName: string) {
    this.uploadedFiles = this.uploadedFiles.filter(f => f.name !== fileName)
  }

  private addScreenshotToDisplay(file: File, blob: Blob, container: Element) {
    const fileElement = document.createElement('div')
    fileElement.className = 'feedlet-thread-attachment'
    fileElement.dataset.fileName = file.name

    fileElement.innerHTML = `
      <div class="feedlet-thread-attachment-preview" id="preview-${Date.now()}"></div>
      <div style="display: flex; align-items: center; gap: 6px; width: 100%;">
        <span>📷</span>
        <div class="feedlet-thread-attachment-name" title="${file.name}">${file.name}</div>
        <button type="button" class="feedlet-thread-attachment-remove" title="Remove">
          <svg width="12" height="12" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
          </svg>
        </button>
      </div>
    `

    // Add remove functionality
    const removeBtn = fileElement.querySelector('.feedlet-thread-attachment-remove')
    if (removeBtn) {
      removeBtn.addEventListener('click', () => {
        this.removeScreenshot(file.name)
        fileElement.remove()
      })
    }

    // Add image preview
    const reader = new FileReader()
    reader.onload = (e) => {
      const preview = fileElement.querySelector(`#preview-${Date.now()}`) as HTMLElement
      if (preview && e.target?.result) {
        preview.innerHTML = `<img src="${e.target.result}" style="width: 100%; height: 60px; object-fit: cover; border-radius: 4px;" alt="Screenshot preview">`
      }
    }
    reader.readAsDataURL(blob)

    container.appendChild(fileElement)
  }

  private removeScreenshot(fileName: string) {
    const index = this.screenshots.findIndex((_, i) => `screenshot-${Date.now()}.png` === fileName)
    if (index > -1) {
      this.screenshots.splice(index, 1)
    }
  }

  private async handleScreenshotSelection() {
    // Create screenshot selection overlay
    const overlay = document.createElement('div')
    overlay.className = 'feedlet-screenshot-overlay'
    overlay.innerHTML = `
      <div class="feedlet-screenshot-instructions">
        <p>Click and drag to select an area to screenshot</p>
        <p>You can take multiple screenshots by clicking the button again</p>
        <p>Press ESC to cancel</p>
      </div>
    `

    // Add overlay styles
    const style = document.createElement('style')
    style.textContent = `
      .feedlet-screenshot-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: rgba(0, 0, 0, 0.3);
        z-index: 2000000;
        cursor: crosshair;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      }

      .feedlet-screenshot-instructions {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: #1a1a1a;
        color: white;
        padding: 20px;
        border-radius: 8px;
        text-align: center;
        pointer-events: none;
      }

      .feedlet-screenshot-instructions p {
        margin: 8px 0;
      }

      .feedlet-screenshot-selection {
        position: absolute;
        border: 2px dashed #4f46e5;
        background: rgba(79, 70, 229, 0.1);
        pointer-events: none;
      }
    `
    document.head.appendChild(style)

    document.body.appendChild(overlay)

    // Handle screenshot selection
    let isSelecting = false
    let startX = 0
    let startY = 0
    let selectionDiv: HTMLElement | null = null

    const handleMouseDown = (e: MouseEvent) => {
      if (e.target !== overlay) return
      isSelecting = true
      startX = e.clientX
      startY = e.clientY
      
      selectionDiv = document.createElement('div')
      selectionDiv.className = 'feedlet-screenshot-selection'
      selectionDiv.style.left = startX + 'px'
      selectionDiv.style.top = startY + 'px'
      overlay.appendChild(selectionDiv)
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (!isSelecting || !selectionDiv) return
      
      const currentX = e.clientX
      const currentY = e.clientY
      
      const left = Math.min(startX, currentX)
      const top = Math.min(startY, currentY)
      const width = Math.abs(currentX - startX)
      const height = Math.abs(currentY - startY)
      
      selectionDiv.style.left = left + 'px'
      selectionDiv.style.top = top + 'px'
      selectionDiv.style.width = width + 'px'
      selectionDiv.style.height = height + 'px'
    }

    const handleMouseUp = async (e: MouseEvent) => {
      if (!isSelecting || !selectionDiv) return
      
      const rect = selectionDiv.getBoundingClientRect()
      if (rect.width > 10 && rect.height > 10) {
        // Hide the overlay temporarily for screenshot
        overlay.style.display = 'none'
        
        try {
          // Import html2canvas dynamically
          const html2canvas = (await import('html2canvas')).default
          
          // Capture the selected area
          const canvas = await html2canvas(document.body, {
            x: rect.left,
            y: rect.top,
            width: rect.width,
            height: rect.height,
            useCORS: true,
            allowTaint: true,
            scale: 1,
            backgroundColor: null
          })
          
          // Convert canvas to blob
          canvas.toBlob((blob) => {
            if (blob) {
              const file = new File([blob], `screenshot-${Date.now()}.png`, { type: 'image/png' })
              this.screenshots.push(blob)
              
              // Add to display
              const attachmentsContainer = this.element?.querySelector('#feedlet-attachments')
              if (attachmentsContainer) {
                this.addScreenshotToDisplay(file, blob, attachmentsContainer)
              }
            }
          }, 'image/png')
          
        } catch (error) {
          console.error('Screenshot capture failed:', error)
          // Fallback to placeholder
          const textarea = this.element?.querySelector('.feedlet-thread-textarea') as HTMLTextAreaElement
          if (textarea) {
            const currentText = textarea.value
            const newText = currentText + (currentText ? '\n' : '') + `📷 Screenshot area selected (${Math.round(rect.width)}×${Math.round(rect.height)})`
            textarea.value = newText
            textarea.dispatchEvent(new Event('input'))
            textarea.focus()
          }
        }
      }
      
      cleanup()
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        cleanup()
      }
    }

    const cleanup = () => {
      isSelecting = false
      overlay.remove()
      style.remove()
      document.removeEventListener('mousedown', handleMouseDown)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.removeEventListener('keydown', handleKeyDown)
    }

    document.addEventListener('mousedown', handleMouseDown)
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    document.addEventListener('keydown', handleKeyDown)

    // Auto-close after 30 seconds
    setTimeout(() => {
      if (document.body.contains(overlay)) {
        cleanup()
      }
    }, 30000)
  }

  private handleSubmit(form: HTMLFormElement) {
    console.log('ExitBug: handleSubmit called')
    const formData = new FormData(form)
    const textarea = form.querySelector('.feedlet-thread-textarea') as HTMLTextAreaElement
    
    const data: any = {
      note: textarea.value.trim(),
      files: this.uploadedFiles,
      screenshots: this.screenshots
    }

    // Collect form data
    for (const [key, value] of formData.entries()) {
      data[key] = value
    }

    // Include contextual data if available
    if (this.currentThreadData) {
      if (this.currentThreadData.marker) {
        data.marker = this.currentThreadData.marker
        data.isContextual = true
      }
      if (this.currentThreadData.viewportInfo) {
        data.viewportInfo = this.currentThreadData.viewportInfo
      }
      // Include thread type for proper handling
      data.threadType = this.currentThreadData.type
    }

    console.log('ExitBug: Form data with contextual info:', data)

    if (!data.note && this.uploadedFiles.length === 0 && this.screenshots.length === 0) {
      console.log('ExitBug: No content to submit - note:', data.note, 'files:', this.uploadedFiles.length, 'screenshots:', this.screenshots.length)
      textarea.focus()
      // Show a subtle error indication
      textarea.style.borderColor = '#ef4444'
      setTimeout(() => {
        textarea.style.borderColor = 'transparent'
      }, 2000)
      return
    }

    // Disable submit button temporarily
    const submitBtn = form.querySelector('.feedlet-thread-submit-btn') as HTMLButtonElement
    if (submitBtn) {
      submitBtn.disabled = true
      submitBtn.innerHTML = `
        <svg width="18" height="18" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 2a8 8 0 100 16 8 8 0 000-16zm0 3a1 1 0 011 1v4a1 1 0 11-2 0V6a1 1 0 011-1zm0 7a1 1 0 100 2 1 1 0 000-2z"></path>
        </svg>
      `
      
      // Re-enable after 2 seconds
      setTimeout(() => {
        submitBtn.disabled = false
        submitBtn.innerHTML = `
          <svg width="18" height="18" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"></path>
          </svg>
        `
      }, 2000)
    }

    // Emit submit event for widget to handle
    this.emit('submit', data)

    // Clear textarea and attachments after successful submission
    setTimeout(() => {
      textarea.value = ''
      textarea.style.height = 'auto'
      this.uploadedFiles = []
      this.screenshots = []
      
      // Clear attachments display
      const attachmentsContainer = this.element?.querySelector('#feedlet-attachments')
      if (attachmentsContainer) {
        attachmentsContainer.innerHTML = ''
      }
      
      textarea.focus()
    }, 100)
  }

  destroy() {
    this.close()
    
    const styles = document.getElementById('feedlet-thread-styles')
    if (styles) {
      styles.remove()
    }
  }
} 